import React, { useState } from 'react';
import authedFetch from '../../lib/authedFetch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Mail, ShieldCheck, Loader2, CheckCircle2, XCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Email the signed audit pack to an auditor/CFO, or verify a pack's authenticity
 * by its Verification ID. Both back the "Signed Audit Pack" trust story.
 */
export const AuditPackDialog = ({ open, onOpenChange, organizationId, defaultTab = 'email' }) => {
  const [tab, setTab] = useState(defaultTab);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sentId, setSentId] = useState('');
  const [vid, setVid] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const orgParam = organizationId && organizationId !== 'all' ? organizationId : undefined;

  const send = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter a valid email address'); return; }
    setSending(true); setSentId('');
    try {
      const r = await authedFetch(`${API_URL}/api/lei-raw/audit-pack/email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: orgParam, recipient_email: email, note: note || undefined }),
      });
      const d = await r.json();
      if (r.ok) { setSentId(d.document_id); toast.success(`Audit pack emailed to ${email}`); }
      else { toast.error(d.detail || 'Could not send'); }
    } catch (e) { toast.error('Could not send the audit pack'); }
    finally { setSending(false); }
  };

  const verify = async () => {
    if (!vid.trim()) { toast.error('Paste a Verification ID'); return; }
    setVerifying(true); setVerifyResult(null);
    try {
      const r = await authedFetch(`${API_URL}/api/lei-raw/audit-pack/verify?document_id=${encodeURIComponent(vid.trim())}`, {});
      setVerifyResult(await r.json());
    } catch (e) { toast.error('Verification failed'); }
    finally { setVerifying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="audit-pack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Signed Audit Pack</DialogTitle>
          <DialogDescription>Email the tamper-evident audit pack to an auditor, or verify one you've received.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-1">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="email" data-testid="audit-pack-tab-email"><Mail className="h-4 w-4 mr-1" /> Email</TabsTrigger>
            <TabsTrigger value="verify" data-testid="audit-pack-tab-verify"><ShieldCheck className="h-4 w-4 mr-1" /> Verify</TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-3 pt-3">
            <div>
              <Label htmlFor="ap-email" className="text-xs">Recipient email</Label>
              <Input id="ap-email" type="email" placeholder="cfo@company.com" value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="audit-pack-email-input" />
            </div>
            <div>
              <Label htmlFor="ap-note" className="text-xs">Note (optional)</Label>
              <Textarea id="ap-note" rows={2} placeholder="Board review copy — full traceability attached."
                value={note} onChange={(e) => setNote(e.target.value)} data-testid="audit-pack-note-input" />
            </div>
            <Button onClick={send} disabled={sending} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="audit-pack-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send signed pack
            </Button>
            {sentId && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800" data-testid="audit-pack-sent-vid">
                Sent. Verification ID: <code className="break-all">{sentId}</code>
              </div>
            )}
          </TabsContent>

          <TabsContent value="verify" className="space-y-3 pt-3">
            <div>
              <Label htmlFor="ap-vid" className="text-xs">Verification ID</Label>
              <Input id="ap-vid" placeholder="Paste the SHA-256 Verification ID from the PDF" value={vid}
                onChange={(e) => setVid(e.target.value)} data-testid="audit-pack-vid-input" />
            </div>
            <Button onClick={verify} disabled={verifying} variant="outline" className="w-full gap-2" data-testid="audit-pack-verify-btn">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verify authenticity
            </Button>
            {verifyResult && (
              verifyResult.valid ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 space-y-1" data-testid="audit-pack-verify-valid">
                  <div className="flex items-center gap-1 font-semibold"><CheckCircle2 className="h-4 w-4" /> Authentic &amp; untampered</div>
                  <div>Generated by <b>{verifyResult.generated_by}</b></div>
                  <div>{verifyResult.measured}/{verifyResult.total} measured · {Math.round((verifyResult.avg_confidence || 0) * 100)}% avg confidence</div>
                  <div className="text-emerald-600">{String(verifyResult.generated_at || '').slice(0, 16).replace('T', ' ')} UTC</div>
                </div>
              ) : (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-700 flex items-center gap-1" data-testid="audit-pack-verify-invalid">
                  <XCircle className="h-4 w-4" /> {verifyResult.detail || 'Not a recognised audit pack.'}
                </div>
              )
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AuditPackDialog;
